<template>
    <div class="w-full">
        <div class="flex flex-row lg:flex-row justify-end" v-if="!is_view">
            <div>
                <a-button data-cy="policy-names-reset-btn" @click="reset()">
                    {{ $t('globals.tables.policy_names.buttons.reset') }}
                </a-button>
                <a-button data-cy="add-policy-name-btn" type="primary" @click="openDrawer('')">
                    {{ $t('globals.tables.policy_names.buttons.add_policy_names') }}
                </a-button>
            </div>
        </div>
        <div class="list-table pt-2 md:pt-4 overflow-x-auto overflow-visible mt-1">
            <div class="w-full">
                <a-table
                    data-cy="policy-names-table"
                    :columns="getColumns"
                    :dataSource="policy_name_data.filter((x) => x.is_a_policy_holder == 1)"
                    :rowKey="(record) => record.id"
                    :pagination="pagination"
                    :locale="locale"
                    :loading="loading"
                    :rowSelection="rowSelection"
                    size="middle"
                    class="ant-table-white"
                >
                    <template #bodyCell="{ column, record, text }">
                        <template v-if="column.key === 'actions'">
                            <a-button-group>
                                <a-tooltip placement="top" :title="$t('globals.tables.policy_names.actions.edit')">
                                    <a-button
                                        data-cy="edit-button"
                                        @click="openDrawer(record.id)"
                                        :disabled="recordsSelected || is_view"
                                    >
                                        <EditOutlined />
                                    </a-button>
                                </a-tooltip>
                                <a-tooltip placement="top" :title="$t('globals.tables.policy_names.actions.delete')">
                                    <a-button
                                        data-cy="delete-button"
                                        class="btn-red"
                                        @click="remove(record)"
                                        :disabled="recordsSelected || is_view"
                                    >
                                        <StopOutlined />
                                    </a-button>
                                </a-tooltip>
                            </a-button-group>
                        </template>
                        <template v-else-if="column.key === 'is_a_driver'">
                            <a-checkbox
                                :disabled="is_view || is_driver"
                                @click="refresh(record)"
                                v-model:checked="
                                    policy_name_data[policy_name_data.findIndex((x) => x.id === record.id)].is_a_driver
                                "
                            />
                        </template>
                        <template v-else>
                            {{ text }}
                        </template>
                    </template>
                </a-table>
                <span v-if="recordsSelected" data-cy="policy-names-table-actions" class="flex pb-2.5 -mt-12">
                    <a-dropdown :trigger="['click']">
                        <a-button @click="(e) => e.preventDefault()" data-cy="btn-mass-selection">
                            {{ $t('globals.tables.policy_names.actions.actions') }}
                            <DownOutlined />
                        </a-button>
                        <template #overlay>
                            <a-menu @click="onActionButtons">
                                <a-menu-item key="delete" data-cy="actions-delete-item">
                                    {{ $t('globals.tables.vehicle_drivers.actions.delete') }}
                                </a-menu-item>
                            </a-menu>
                        </template>
                    </a-dropdown>
                </span>
            </div>
        </div>
        <a-drawer
            data-cy="policy-names-drawer"
            :title="$t('covers.forms.policy_holder.title_holder')"
            :width="625"
            :open="drawer_visible"
            :body-style="{ padding: '0px', paddingBottom: '10px' }"
            :destroyOnClose="true"
            :maskClosable="false"
            @close="onClose"
        >
            <form-policy-holder
                data-cy="policy-holder-form"
                :policy_holder="policy_holder_data"
                :processing="processing"
                :validation="validation"
                :insurer_id="cover.insurer_id"
                :intermediary_id="cover.company_id"
                :is_driver="is_driver"
                @is-form-dirty="isDrawerFormDirty"
                @formSubmit="onSave"
                @onDrawerClose="onClose"
                form_mode="drawer"
            >
            </form-policy-holder>
        </a-drawer>
    </div>
</template>

<script>
import FormPolicyHolder from '@/components/forms/PolicyHolder.vue';
import { DownOutlined, EditOutlined, StopOutlined } from '@ant-design/icons-vue';

export default {
    components: {
        FormPolicyHolder,
        DownOutlined,
        EditOutlined,
        StopOutlined,
    },
    data() {
        return {
            loading: false,
            pagination: {
                pageSize: 5,
                showSizeChanger: false,
                total: 0,
            },
            locale: {
                emptyText: this.$t('globals.tables.no_data'),
            },
            processing: false,
            current_filter: {},
            sorter: {},
            params: {},
            selected_row_keys: [],
            select_all: false,
            policy_holder_data: {},
            drawer_visible: false,
            drawer_form_dirty: false,
            validation: {
                errors: {},
                message: '',
            },
        };
    },
    props: {
        policy_name_data: Array,
        cover: Object,
        is_view: {
            type: Boolean,
            default: false,
        },
        is_driver: {
            type: Boolean,
            default: false,
        },
    },
    computed: {
        getColumns() {
            return [
                {
                    key: 'actions',
                    width: 35,
                },
                {
                    title: this.$t('globals.tables.policy_names.headers.national_id'),
                    key: 'national_id',
                    dataIndex: 'national_id',
                },
                {
                    title: this.$t('globals.tables.policy_names.headers.name'),
                    key: 'name',
                    dataIndex: 'name',
                },
                {
                    title: this.$t('globals.tables.policy_names.headers.is_a_driver'),
                    key: 'is_a_driver',
                    dataIndex: 'is_a_driver',
                },
            ];
        },
        recordsSelected() {
            return this.selected_row_keys.length > 1;
        },
        rowSelection() {
            return {
                onChange: (selected_row_keys) => {
                    this.selected_row_keys = selected_row_keys;
                },
                getCheckboxProps: () => ({
                    props: {
                        disabled: this.is_view,
                    },
                }),
            };
        },
    },
    methods: {
        remove(value) {
            this.$emit('change', {
                type: 'remove',
                data: this.policy_name_data.filter((x) => x.id != value.id),
            });
        },
        openDrawer(id) {
            if (id == '') {
                this.policy_holder_data = {
                    id: null,
                    is_a_company: false,
                    is_a_policy_holder: true,
                    is_a_driver: false,
                    is_excluded: false,
                    is_main_driver: false,
                    name: '',
                    national_id: '',
                };

                this.validation = {
                    errors: {},
                    message: '',
                };

                this.drawer_visible = true;
            } else {
                this.policy_name_data.forEach((record) => {
                    if (record.id == id) {
                        this.policy_holder_data = { ...record };
                    }
                });

                this.validation = {
                    errors: {},
                    message: '',
                };

                this.drawer_visible = true;
            }
        },
        onClose() {
            this.$emit('change', { type: 'cancel' });
            this.drawer_form_dirty = false;
            this.drawer_visible = false;
        },
        onSave(data) {
            this.$emit('add', data);
        },
        isDrawerFormDirty(is_dirty) {
            this.drawer_form_dirty = is_dirty;
        },
        reset() {
            this.$emit('change', { type: 'reset' });
        },
        onActionButtons(event, type) {
            switch (event.key) {
                case 'delete':
                    let ids = this.selected_row_keys;
                    this.$emit('change', {
                        type: 'remove',
                        data: this.policy_name_data.filter((x) => !ids.includes(x.id)),
                    });
                    this.selected_row_keys = [];
                    break;
            }
        },
        refresh(value) {
            if (value != false) {
                this.policy_name_data.map((record) => {
                    setTimeout(() => {
                        if (!value.is_a_driver && value.is_main_driver && value.id == record.id) {
                            record.is_main_driver = false;
                        } else if (
                            value.is_a_driver &&
                            value.is_main_driver &&
                            record.is_main_driver &&
                            value.id !== record.id
                        ) {
                            record.is_main_driver = false;
                        }
                        return record;
                    });
                }, 50);
            }
            setTimeout(() => {
                let data = this.policy_name_data.splice(this.policy_name_data.length);
                this.policy_name_data.concat([data]);
            }, 50);
        },
    },
};
</script>
